import { formatLocalFiatAmount } from '@/config/marketPresentation'
import i18n from '@/utils/i18n'
import { toCurrency } from '@/utils/numbers'

export const JBP_DEPOSITS_ENABLED = process.env.EXPO_PUBLIC_JBP_DEPOSITS_ENABLED === 'true'
export const JBP_WITHDRAWALS_ENABLED = process.env.EXPO_PUBLIC_JBP_WITHDRAWALS_ENABLED === 'true'

export type JbpPaymentMethodKind = 'BANK_CARD' | 'ALIPAY' | 'WECHAT'
export type JbpFeeMode = 'PERCENTAGE' | 'FLAT'

export type JbpPaymentMethodSetting = {
  method: JbpPaymentMethodKind
  bankId: string
  vipLevel: number
  enabled: boolean
  feeMode: JbpFeeMode
  transactionFeeRate: string
  transactionFeeFlat: string
  minAmount: string
  maxAmount: string
}

export type JbpFeeSnapshot = {
  feeMode?: JbpFeeMode | null
  transactionFeeRate?: string | null
  transactionFeeFlat?: string | null
  transactionFeeAmount?: string | number | null
}

export type JbpDepositInvoice = {
  invoiceId: string
  companyOrderNum: string
  mownecumOrderNum?: string | null
  walletCreditId?: string | null
  qrCodeImageUrl?: string | null
  checkoutUrl?: string | null
  accountName?: string | null
  accountNum?: string | null
  bankName?: string | null
  bankAddress?: string | null
  bankId: string
  requireAmount?: string | null
  state: string
  requestedAmount: string
  paidAmount: string
  creditedAmount: string
  feeMode?: JbpFeeMode | null
  transactionFeeRate?: string | null
  transactionFeeFlat?: string | null
  transactionFeeAmount?: string | null
  settledBy?: string | null
  statusMessage?: string | null
  callbackCount: number
  callbackVerified: boolean
  timeoutAt?: string | null
  closedAt?: string | null
  createdAt: string
  updatedAt: string
  settledAt?: string | null
}

export type CurrentVipRecordQuery = {
  getCurrentVipRecord?: {
    level: number
  } | null
}

export type JbpPaymentMethodSettingsQuery = {
  getJbpPaymentMethodSettings?: {
    data: JbpPaymentMethodSetting[]
  } | null
}

export function getEligibleJbpSettings(settings: JbpPaymentMethodSetting[], amount: number, vipLevel: number) {
  if (!Number.isFinite(amount) || amount <= 0) {
    return []
  }

  return getAvailableJbpSettingsForVip(settings, vipLevel).filter((setting) => {
    const minAmount = Number(setting.minAmount)
    const maxAmount = Number(setting.maxAmount)

    return Number.isFinite(minAmount) && Number.isFinite(maxAmount) && amount >= minAmount && amount <= maxAmount
  })
}

export function getAvailableJbpSettingsForVip(settings: JbpPaymentMethodSetting[], vipLevel: number) {
  return settings.filter((setting) => setting.enabled && setting.vipLevel === vipLevel)
}

export function getJbpPaymentMethodLabel(method: JbpPaymentMethodKind) {
  return i18n.t(`fastPay:paymentMethod.${method}`)
}

export function formatJbpFeeLabel(setting: JbpPaymentMethodSetting, amount: number) {
  const fee = calculateJbpFee(setting, amount)
  if (setting.feeMode === 'FLAT') {
    return i18n.t('fastPay:flatFee', { amount: formatJbpCurrency(fee) })
  }

  return `${toCurrency(setting.transactionFeeRate)}% / ${formatJbpCurrency(fee)}`
}

export function formatJbpConfiguredFeeLabel(setting: JbpPaymentMethodSetting) {
  if (setting.feeMode === 'FLAT') {
    return i18n.t('fastPay:flatFee', { amount: formatJbpCurrency(setting.transactionFeeFlat) })
  }

  return `${toCurrency(setting.transactionFeeRate)}%`
}

export function formatJbpRangeLabel(setting: JbpPaymentMethodSetting) {
  return `${formatJbpCurrency(setting.minAmount)} - ${formatJbpCurrency(setting.maxAmount)}`
}

export function formatJbpFeeSnapshotLabel(snapshot: JbpFeeSnapshot) {
  const feeAmount = snapshot.transactionFeeAmount ?? snapshot.transactionFeeFlat ?? 0
  if (snapshot.feeMode === 'FLAT') {
    return i18n.t('fastPay:flatFee', { amount: formatJbpCurrency(feeAmount) })
  }

  if (snapshot.feeMode === 'PERCENTAGE' && snapshot.transactionFeeRate) {
    return `${toCurrency(snapshot.transactionFeeRate)}% / ${formatJbpCurrency(feeAmount)}`
  }

  return formatJbpCurrency(feeAmount)
}

export function calculateJbpFee(setting: JbpPaymentMethodSetting, amount: number) {
  const rawFee =
    setting.feeMode === 'FLAT'
      ? Number(setting.transactionFeeFlat)
      : (amount * Number(setting.transactionFeeRate)) / 100

  if (!Number.isFinite(rawFee) || rawFee < 0) {
    return 0
  }

  return Math.min(Math.max(0, amount), Math.round((rawFee + Number.EPSILON) * 100) / 100)
}

export function calculateJbpNetCredit(setting: JbpPaymentMethodSetting, amount: number) {
  const fee = calculateJbpFee(setting, amount)
  return Math.round((Math.max(0, amount - fee) + Number.EPSILON) * 100) / 100
}

export function formatJbpCurrency(amount: string | number | null | undefined) {
  return formatLocalFiatAmount((amount ?? 0).toString())
}

export function buildJbpIdempotencyKey(prefix: string) {
  const randomUUID = globalThis.crypto?.randomUUID?.()
  if (randomUUID) {
    return `${prefix}-${randomUUID}`
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
